import React, { useState } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, IconButton, Chip
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dashboard from '../../components/containers/Dashboard/Dashboard';
import { loadVersionToDraft } from './actions/versionsActions';
import { isUnlimited } from '../../utils/licenseUtils';

/**
 * VersionsButton - כפתור וצפייה בגירסאות אחרונות
 * Business Component - סטנד-אלון, מטפל בעצמו ב-Redux
 * 
 * @param {Array} versions - מערך הגירסאות
 * @param {boolean} disabled - האם הכפתור מושבת
 * @param {string} currentVersionId - מזהה הגירסה הנוכחית (אם קיים)
 * @param {object} organization - ארגון (לצורך הצגת הדשבורד)
 * @param {object} catalog - קטלוג הווידג'טים
 * @param {function} dispatch - Redux dispatch
 * @param {function} onLoadVersion - callback אופציונלי (לטוסט)
 */
const VersionsButton = ({
    versions = [],
    disabled = false,
    currentVersionId = null,
    organization = null,
    catalog = null,
    dispatch = null,
    onLoadVersion = null,
    versionLimit = 9999
}) => {
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleSelectVersion = (version) => {
        if (dispatch) {
            console.log('📥 Loading version to draft:', version);
            dispatch(loadVersionToDraft(version));

            // קריאה ל-callback אם קיים (לטוסט)
            if (onLoadVersion) {
                onLoadVersion(version);
            }
        }
        handleClose();
    };

    // קח את 5 הגירסאות האחרונות
    const recentVersions = [...versions].reverse();

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleOpen}
                disabled={disabled || versions.length === 0}
                sx={{
                    borderColor: 'rgba(30,58,95,0.45)',
                    color: '#1e3a5f',
                    fontWeight: 500,
                    px: 2,
                    py: 0.5,
                    fontSize: '0.875rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    minHeight: '36px',
                    mx: 1,
                    gap: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: '#1e3a5f',
                        bgcolor: 'rgba(30,58,95,0.05)',
                        boxShadow: '0 2px 10px rgba(30,58,95,0.15)',
                        transform: 'translateY(-1px)'
                    },
                    '&.Mui-disabled': {
                        borderColor: 'rgba(30,58,95,0.3)',
                        color: 'rgba(30,58,95,0.3)'
                    }
                }}
            >
                גירסאות
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        maxHeight: '90vh',
                        width: '95vw',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(30,58,95,0.08)'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    pb: 2,
                    background: 'linear-gradient(135deg, #112236 0%, #1e3a5f 100%)',
                    color: 'white',
                    direction: 'rtl'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexDirection: 'row' }}>
                        <HistoryIcon sx={{ color: 'white' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            גירסאות אחרונות
                        </Typography>
                        <Chip
                            label={isUnlimited(versionLimit)
                                ? `${versions.length} גרסאות`
                                : `${versions.length}/${versionLimit} גרסאות`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                                '& .MuiChip-label': { px: 1 }
                            }}
                        />
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    {!isUnlimited(versionLimit) && versions.length >= versionLimit && (
                        <Box sx={{
                            p: 2, mb: 2, borderRadius: '12px',
                            background: 'linear-gradient(135deg, rgba(154,114,9,0.08), rgba(154,114,9,0.03))',
                            border: '1px solid rgba(154,114,9,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            direction: 'rtl'
                        }}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e3a5f' }}>
                                    הגעת למגבלת הגרסאות במסלול שלך ({versionLimit})
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#5a6978' }}>
                                    פרסום גרסה חדשה ימחק את הגרסה הישנה ביותר. שדרג לשמירת גרסאות נוספות.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: 'היסטוריית גרסאות', requiredPlan: 'pro' } });
                                    handleClose();
                                }}
                                sx={{
                                    bgcolor: '#9a7209', '&:hover': { bgcolor: '#b8860b' },
                                    textTransform: 'none', borderRadius: '8px',
                                    whiteSpace: 'nowrap', ml: 2, flexShrink: 0
                                }}
                            >
                                שדרג מסלול
                            </Button>
                        </Box>
                    )}

                    {recentVersions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                                אין גירסאות שמורות
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 3
                        }}>
                            {recentVersions.map((version, index) => {
                                const isLatest = index === 0;
                                const isCurrent = version.versionId === currentVersionId;

                                // טיפול נכון ב-Firestore Timestamp (שדה נשמר כ-createdAt)
                                const rawDate = version.createdAt || version.publishedAt;
                                let versionDate;
                                if (rawDate?.toDate) {
                                    versionDate = rawDate.toDate();
                                } else if (rawDate?.seconds) {
                                    versionDate = new Date(rawDate.seconds * 1000);
                                } else if (rawDate) {
                                    versionDate = new Date(rawDate);
                                } else {
                                    versionDate = new Date();
                                }

                                return (
                                    <Box
                                        key={version.versionId}
                                        sx={{
                                            border: '2px solid',
                                            borderColor: isCurrent ? 'rgba(30,58,95,0.5)' : 'rgba(30,58,95,0.12)',
                                            borderRadius: '14px',
                                            overflow: 'hidden',
                                            transition: 'all 0.25s',
                                            boxShadow: isCurrent ? '0 0 0 3px rgba(30,58,95,0.08)' : 'none',
                                            '&:hover': {
                                                borderColor: '#1e3a5f',
                                                boxShadow: '0 6px 20px rgba(30,58,95,0.14)',
                                                transform: 'translateY(-2px)'
                                            }
                                        }}
                                    >
                                        {/* Header */}
                                        <Box sx={{
                                            p: 2,
                                            background: isCurrent
                                                ? 'linear-gradient(135deg, rgba(30,58,95,0.07) 0%, rgba(30,58,95,0.03) 100%)'
                                                : 'rgba(30,58,95,0.025)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    גירסה #{index + 1}
                                                </Typography>
                                                {isLatest && (
                                                    <Chip
                                                        label="אחרונה"
                                                        size="small"
                                                        sx={{
                                                            height: '20px',
                                                            fontSize: '0.7rem',
                                                            background: 'linear-gradient(135deg, #9a7209 0%, #c8960c 100%)',
                                                            color: 'white',
                                                            fontWeight: 700,
                                                            boxShadow: '0 2px 6px rgba(154,114,9,0.3)',
                                                            '& .MuiChip-label': { px: 1 }
                                                        }}
                                                    />
                                                )}
                                                {isCurrent && (
                                                    <Chip
                                                        icon={<CheckCircleIcon sx={{ fontSize: '14px', color: '#1e3a5f !important' }} />}
                                                        label="טיוטה נוכחית"
                                                        size="small"
                                                        sx={{
                                                            height: '20px',
                                                            fontSize: '0.7rem',
                                                            bgcolor: 'rgba(30,58,95,0.1)',
                                                            border: '1px solid rgba(30,58,95,0.25)',
                                                            color: '#1e3a5f',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { px: 0.75 }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" dir="rtl">
                                                {versionDate.toLocaleString('he-IL', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </Typography>
                                        </Box>

                                        {/* Preview - תצוגה אמיתית של הדשבורד */}
                                        <Box sx={{
                                            p: 2,
                                            bgcolor: '#f5f5f5',
                                            minHeight: '400px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '380px',
                                                    border: '2px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                    bgcolor: '#fff',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}
                                            >
                                                {/* Dashboard Component עם הגירסה */}
                                                {dispatch && organization && catalog ? (
                                                    <Dashboard
                                                        dispatch={dispatch}
                                                        organization={organization}
                                                        catalog={catalog}
                                                        dashboard={{
                                                            current: {
                                                                widgets: version.widgets || [],
                                                                settings: version.settings || { bgColor: '#fcfbfa', bgImage: '' }
                                                            }
                                                        }}
                                                        isEditMode={false}
                                                        style={{
                                                            pointerEvents: 'none', // מניעת אינטראקציה
                                                            userSelect: 'none'
                                                        }}
                                                    />
                                                ) : (
                                                    // Fallback - תצוגה פשוטה אם אין את הנתונים הדרושים
                                                    <Box sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        bgcolor: version.settings?.bgColor || '#fcfbfa',
                                                        backgroundImage: version.settings?.bgImage ? `url(${version.settings.bgImage})` : 'none',
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        p: 2
                                                    }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {version.widgets?.length || 0} ווידג'טים
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Footer */}
                                        <Box sx={{
                                            p: 2,
                                            bgcolor: '#fafafa',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {version.widgets?.length || 0} ווידג'טים
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={isCurrent}
                                                onClick={() => handleSelectVersion(version)}
                                                sx={{
                                                    bgcolor: '#1e3a5f',
                                                    '&:hover': { bgcolor: '#2d5a8f' },
                                                    textTransform: 'none',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                {isCurrent ? 'גירסה נוכחית' : 'טען לטיוטה'}
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        💡 בחירת גירסה תטען אותה לטיוטה. לחץ "שמור" כדי לשמור את השינויים.
                    </Typography>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            bgcolor: '#1e3a5f',
                            color: 'white',
                            '&:hover': {
                                bgcolor: '#2d5a8f'
                            }
                        }}
                    >
                        סגור
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VersionsButton;
